import React, { useEffect, useMemo, useState } from 'react';
import { Button, Divider, Grid, RadioGroup, TextField } from '@material-ui/core';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import Modal from '../../components/Modal/modal';
import RadioOption from '../../components/FormOptions/RadioOption';
import { useLang } from '../../util/lang';

const MATERIAL_SHADE_OPTIONS = ['A1', 'A2', 'A3', 'A3.5', 'B1', 'BW'];
const FIT_RESTORATION_TYPES = ['option.formaInjectionTechnique', 'option.flowableInjectionTechnique'];

export const RESTORATION_FORM_DEFAULT_VALUES = {
    restorationType: '',
    biofunctionalMockup: '',
    materialShade: '',
    gingivectomyGuide: '',
    restorationRemark: '',
};

export const RESTORATION_FORM_FIELD_NAMES = Object.keys(RESTORATION_FORM_DEFAULT_VALUES);

/** Builds values that match the Prescription Note Q1 options. */
const getRestorationTypeOptions = (isMarieGroup, isTWGroup, get) => [
    {
        value: isMarieGroup ? 'option.formaInjectionTechnique' : 'option.flowableInjectionTechnique',
        label: isMarieGroup
            ? get('@PatientInfo.smileDesign.prescriptionNotes.option.formaInjectionTechnique')
            : get('@PatientInfo.smileDesign.prescriptionNotes.option.flowableInjectionTechnique'),
        disabled: isTWGroup,
    },
    { value: 'option.ceramicVeneersCrowns', label: get('@PatientInfo.smileDesign.prescriptionNotes.option.ceramicVeneersCrowns') },
    {
        value: 'option.guidedVeneerTreatment',
        label: get('@PatientInfo.smileDesign.prescriptionNotes.option.guidedVeneerTreatment'),
        disabled: !isMarieGroup,
    },
];

/** Renders restoration form fields. */
const RestorationFormFields = ({
    isMockupOrdering = false,
    isMarieGroup: isMarieGroupProp = undefined,
    isTWGroup: isTWGroupProp = undefined,
    isSEAGroup = false,
}) => {
    const get = useLang();
    const methods = useFormContext();
    const formValues = useWatch({ control: methods.control });
    const storedIsMarieGroup = useMemo(() => JSON.parse(localStorage.getItem('isMarieGroup')), []);
    const storedIsTWGroup = useMemo(() => JSON.parse(localStorage.getItem('isTWGroup')), []);
    const isMarieGroup = typeof isMarieGroupProp === 'boolean' ? isMarieGroupProp : storedIsMarieGroup;
    const isTWGroup = typeof isTWGroupProp === 'boolean' ? isTWGroupProp : storedIsTWGroup;
    const restorationTypeOptions = useMemo(() => getRestorationTypeOptions(isMarieGroup, isTWGroup, get), [isMarieGroup, isTWGroup, get]);
    const [pendingRestorationType, setPendingRestorationType] = useState('');
    const [openRestorationTypeConfirm, setOpenRestorationTypeConfirm] = useState(false);
    const isFitRestorationType = FIT_RESTORATION_TYPES.includes(formValues.restorationType);
    const hasDependentRestorationAnswers = Boolean(
        formValues.biofunctionalMockup ||
        formValues.materialShade ||
        formValues.gingivectomyGuide ||
        formValues.restorationRemark
    );

    useEffect(() => {
        /** Sets the required default mock-up option for Mock-up Ordering. */
        function setMockupOrderingDefault() {
            if (
                isMockupOrdering &&
                formValues.restorationType &&
                !isFitRestorationType &&
                !formValues.biofunctionalMockup
            ) {
                methods.setValue('biofunctionalMockup', 'yes', { shouldValidate: true });
            }
        }

        setMockupOrderingDefault();
    }, [formValues.biofunctionalMockup, formValues.restorationType, isFitRestorationType, isMockupOrdering, methods]);

    /** Clears answers that depend on the selected restoration type. */
    const clearRestorationAnswers = () => {
        RESTORATION_FORM_FIELD_NAMES.slice(1).forEach((fieldName) => {
            methods.setValue(fieldName, '', { shouldValidate: true });
        });
    };

    /** Applies a restoration type after the user confirms replacing dependent answers. */
    const applyRestorationType = (restorationType) => {
        methods.setValue('restorationType', restorationType, { shouldValidate: true });
        clearRestorationAnswers();
        methods.trigger();
    };

    /** Requests confirmation before changing a restoration type with existing answers. */
    const handleRestorationTypeChange = (restorationType) => {
        if (restorationType === formValues.restorationType) return;

        if (hasDependentRestorationAnswers) {
            setPendingRestorationType(restorationType);
            setOpenRestorationTypeConfirm(true);
            return;
        }

        applyRestorationType(restorationType);
    };

    return (
        <>
            <Grid item>
                <div style={{ marginBottom: 16, fontSize: 17, fontWeight: 600 }}>
                    {get('@PatientInfo.smileDesign.clinicalApprovement.Restoration.SectionOne')}
                </div>
                <div className="MuiFormLabel-root" style={{ marginBottom: 8 }}>
                    {get('@PatientInfo.smileDesign.clinicalApprovement.Restoration.typeQuestion')}{' '}
                    <span style={{ color: '#f44336' }}>*</span>
                </div>
                <Controller
                    control={methods.control}
                    name="restorationType"
                    render={({ field }) => (
                        <RadioGroup value={field.value} style={{ alignItems: 'flex-start' }}>
                            {restorationTypeOptions.map((option) => (
                                <RadioOption
                                    key={option.value}
                                    value={option.value}
                                    label={option.label}
                                    selected={field.value === option.value}
                                    onChange={(event) => handleRestorationTypeChange(event.target.value)}
                                    disabled={option.disabled}
                                />
                            ))}
                        </RadioGroup>
                    )}
                />
                <div style={{ color: '#6b6f8b', fontSize: 13, fontStyle: 'italic' }}>
                    {get('@PatientInfo.smileDesign.clinicalApprovement.Restoration.typeQuestionRemark')}
                </div>

                <Divider style={{ margin: '28px 0 24px' }} />
                <div style={{ marginBottom: 16, fontSize: 17, fontWeight: 600 }}>
                    {get('@PatientInfo.smileDesign.clinicalApprovement.Restoration.SectionTwo')}
                </div>
                <div className="MuiFormLabel-root" style={{ marginBottom: 8 }}>
                    {get('@PatientInfo.smileDesign.clinicalApprovement.Restoration.orderBiofunctionalMockup')}{' '}
                    <span style={{ color: '#f44336' }}>*</span>
                </div>
                <Controller
                    control={methods.control}
                    name="biofunctionalMockup"
                    render={({ field }) => (
                        <RadioGroup value={field.value} style={{ alignItems: 'flex-start' }}>
                            {isFitRestorationType && !(isMockupOrdering && isSEAGroup) ? (
                                <>
                                    <RadioOption
                                        value="mockupKey"
                                        label={get(
                                            '@PatientInfo.smileDesign.clinicalApprovement.Restoration.mockUpKeyOnly'
                                        )}
                                        selected={field.value === 'mockupKey'}
                                        onChange={field.onChange}
                                        disabled={!formValues.restorationType}
                                    />
                                    <RadioOption
                                        value="mockupKeyAndMaterial"
                                        label={get(
                                            '@PatientInfo.smileDesign.clinicalApprovement.Restoration.mockUpKeyAndMaterial'
                                        )}
                                        selected={field.value === 'mockupKeyAndMaterial'}
                                        onChange={field.onChange}
                                        disabled={!formValues.restorationType}
                                    />
                                    <RadioOption
                                        value="no"
                                        label={get('@PatientInfo.smileDesign.clinicalApprovement.Restoration.no')}
                                        selected={field.value === 'no'}
                                        onChange={field.onChange}
                                        disabled={!formValues.restorationType || isMockupOrdering}
                                    />
                                </>
                            ) : (
                                <>
                                    <RadioOption
                                        value="yes"
                                        label={get('@PatientInfo.smileDesign.clinicalApprovement.Restoration.yes')}
                                        selected={field.value === 'yes'}
                                        onChange={field.onChange}
                                        disabled={!formValues.restorationType}
                                    />
                                    <RadioOption
                                        value="no"
                                        label={get('@PatientInfo.smileDesign.clinicalApprovement.Restoration.no')}
                                        selected={field.value === 'no'}
                                        onChange={field.onChange}
                                        disabled={!formValues.restorationType || isMockupOrdering}
                                    />
                                </>
                            )}
                        </RadioGroup>
                    )}
                />
                <div style={{ color: '#6b6f8b', fontSize: 13, fontStyle: 'italic' }}>
                    {get('@PatientInfo.smileDesign.clinicalApprovement.Restoration.mockUpKeyWithMockUpRemark')}
                </div>

                {isFitRestorationType && formValues.biofunctionalMockup === 'mockupKeyAndMaterial' && (
                    <div style={{ marginTop: 20 }}>
                        <div className="MuiFormLabel-root" style={{ marginBottom: 8 }}>
                            {get('@PatientInfo.smileDesign.clinicalApprovement.Restoration.materialShade')}{' '}
                            <span style={{ color: '#f44336' }}>*</span>
                        </div>
                        <Controller
                            control={methods.control}
                            name="materialShade"
                            render={({ field }) => (
                                <div className="restorative-shade-selector">
                                    <div className="restorative-shade-options restorative-material-shade-options">
                                        {MATERIAL_SHADE_OPTIONS.map((shade) => (
                                            <button
                                                key={shade}
                                                type="button"
                                                className={`restorative-shade-chip${field.value === shade ? ' restorative-shade-chip-selected' : ''}`}
                                                onClick={() => field.onChange(shade)}
                                            >
                                                {shade}
                                            </button>
                                        ))}
                                        <Button
                                            size="small"
                                            style={{
                                                marginLeft: 8,
                                                minWidth: 72,
                                                height: 38,
                                                borderRadius: 8,
                                                color: 'black',
                                                backgroundColor: '#fff',
                                                border: '1px solid rgba(0, 0, 0, 0.23)',
                                                padding: '8px 16px',
                                                fontSize: 14,
                                            }}
                                            onClick={() => methods.setValue('materialShade', '', { shouldValidate: true })}
                                        >
                                            Reset
                                        </Button>
                                </div>
                                </div>
                            )}
                        />
                       
                        <div style={{ color: '#6b6f8b', fontSize: 13, fontStyle: 'italic', marginTop: 8 }}>
                            {get('@PatientInfo.smileDesign.clinicalApprovement.Restoration.gingivectomyGuide')}
                        </div>
                    </div>
                )}

                <div className="MuiFormLabel-root" style={{ marginTop: 24, marginBottom: 8 }}>
                    {get('@PatientInfo.smileDesign.clinicalApprovement.Restoration.orderGingivectomyGuide')}{' '}
                    <span style={{ color: '#f44336' }}>*</span>
                </div>
                <Controller
                    control={methods.control}
                    name="gingivectomyGuide"
                    render={({ field }) => (
                        <RadioGroup value={field.value} style={{ alignItems: 'flex-start' }}>
                            <RadioOption
                                value="yes"
                                label={get('@PatientInfo.smileDesign.clinicalApprovement.Restoration.yes')}
                                selected={field.value === 'yes'}
                                onChange={field.onChange}
                            />
                            <RadioOption
                                value="no"
                                label={get('@PatientInfo.smileDesign.clinicalApprovement.Restoration.no')}
                                selected={field.value === 'no'}
                                onChange={field.onChange}
                            />
                        </RadioGroup>
                    )}
                />
                <div style={{ color: '#6b6f8b', fontSize: 13, fontStyle: 'italic' }}>
                    {get('@PatientInfo.smileDesign.clinicalApprovement.Restoration.gingivectomyGuideRemark')}
                </div>

                {formValues.biofunctionalMockup && formValues.biofunctionalMockup !== 'no' && (
                    <div style={{ marginTop: 24 }}>
                        <div className="MuiFormLabel-root" style={{ marginBottom: 8 }}>
                            {get('@PatientInfo.smileDesign.clinicalApprovement.Restoration.Remark')}
                        </div>
                        <Controller
                            control={methods.control}
                            name="restorationRemark"
                            render={({ field }) => (
                                <TextField {...field} multiline rows={4} variant="outlined" fullWidth />
                            )}
                        />
                    </div>
                )}
            </Grid>

            <Modal
                title=""
                open={openRestorationTypeConfirm}
                maxWidth="sm"
                handleClose={() => setOpenRestorationTypeConfirm(false)}
                submitButton={true}
                submitButtonLabel="Continue"
                handleDataSave={() => {
                    applyRestorationType(pendingRestorationType);
                    setPendingRestorationType('');
                    setOpenRestorationTypeConfirm(false);
                }}
            >
                {get('@PatientInfo.smileDesign.clinicalApprovement.Restoration.mockUpmodel')}
            </Modal>
        </>
    );
};

export default RestorationFormFields;
